/**
 * @module components/DescriptionSetting
 * @description 表格描述编辑（对齐 ChartsDrop EditDescription + EditDescriptionModal）
 *
 * 状态管理模式：
 *   读: useSettingObjectState('description' / 'column_percentage')
 *   列: columnFilters/columnDimensions/columnMetrics/columnRawColumns 来自 Query
 *   写: setDescription
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'umi';
import { DATALAB_COMPARE_USER_FILTERS } from '@/pages/datalab/components/UserFilter/FilterPanel/constant';
import type { IColumnInfo } from '@dbl/core/dataSource';
import { ChartCalculateMode, type TChartCalculateMode } from '@dbl/core/chartEditor';
import { useSettingObjectState } from '../../context';
import { useQuerySelector } from '../../hooks/deps';
import { EMPTY_COLUMN_INFO_LIST } from '../../utils/constants';
import EditDescriptionModal from './EditDescriptionModal';
import type {
  IDescriptionColumnItem,
  IDescriptionSetting,
} from '../../types/settings/description';
import './index.scss';

interface DescriptionSettingProps {
  visibleItems?: Record<string, boolean>;
}

const DescriptionSetting: React.FC<DescriptionSettingProps> = () => {
  const { formatMessage } = useIntl();
  const [visible, setVisible] = useState(false);

  const columnDimensions = useQuerySelector(
    s => s.columnDimensions ?? EMPTY_COLUMN_INFO_LIST,
  );
  const columnMetrics = useQuerySelector(
    s => s.columnMetrics ?? EMPTY_COLUMN_INFO_LIST,
  );
  const columnUserFilters = useQuerySelector(
    s => s.columnUserFilters ?? EMPTY_COLUMN_INFO_LIST,
  );
  const mode = useQuerySelector(s => s.mode as TChartCalculateMode);

  const columnFilters = useQuerySelector(
    s => s.columnFilters ?? EMPTY_COLUMN_INFO_LIST,
  );
  const columnRawColumns = useQuerySelector(
    s => (s.columnRawColumns as IColumnInfo[] | undefined) ?? EMPTY_COLUMN_INFO_LIST,
  );

  const { value: description, setValue: setDescription } =
    useSettingObjectState('description');
  const { value: columnPercentage } = useSettingObjectState('column_percentage');

  const isRawColumn = mode === ChartCalculateMode.RawColumn;

  const newMetrics = useMemo((): IDescriptionColumnItem[] => {
    const metrics = [...((columnMetrics || []) as IColumnInfo[])];
    columnPercentage?.forEach(column => {
      let findMetric: IColumnInfo | undefined;
      const index = metrics.findIndex(item => {
        if (
          (item.table_code || 't0') === column.table_code &&
          item.column_name === column.metric
        ) {
          findMetric = item;
          return true;
        }
        return false;
      });
      if (index !== -1) {
        metrics.splice(index + 1, 0, {
          table_code: column.table_code || 't0',
          column_display: `% ${findMetric?.column_display || column.metric}`,
          column_name: `% ${column.metric}`,
          table_id: findMetric?.table_id || '',
        } as IColumnInfo);
      }
    });
    return metrics;
  }, [columnMetrics, columnPercentage]);

  const newDimensions = useMemo((): IDescriptionColumnItem[] => {
    const dimensions = [...((columnDimensions || []) as IColumnInfo[])];
    const userFiltersObj: IDescriptionColumnItem = {
      table_code: 't0',
      column_name: DATALAB_COMPARE_USER_FILTERS,
      table_id: '',
      column_display: formatMessage({ id: 'dbl_filter_comparison' }),
    };
    if ((columnUserFilters || []).length > 1) {
      dimensions.unshift(userFiltersObj as IColumnInfo);
    }
    return dimensions;
  }, [columnDimensions, columnUserFilters, formatMessage]);

  const dimensionsForModal = isRawColumn ? [] : newDimensions;
  const metricsForModal = isRawColumn
    ? (columnRawColumns as IDescriptionColumnItem[])
    : newMetrics;

  const handleChange = useCallback(
    (newValue: IDescriptionSetting) => {
      setDescription(newValue);
    },
    [setDescription],
  );

  return (
    <div className="dbl-chart-description">
      <div className="dbl-chart-description-head">
        <div>{formatMessage({ id: 'dbl_chart_primary_description' })}</div>
        <div className="dbl-link-style" onClick={() => setVisible(true)}>
          {formatMessage({ id: 'dbl_common_edit' })}
        </div>
      </div>
      <EditDescriptionModal
        visible={visible}
        setVisible={setVisible}
        filters={columnFilters}
        dimensions={dimensionsForModal}
        metrics={metricsForModal}
        initValues={description}
        onChange={handleChange}
      />
    </div>
  );
};

export default React.memo(DescriptionSetting);
